import {
  type CreateFolderRequest,
  type DeleteFolderRequest,
  type FolderDeletePreview,
  type FolderDeleteResult,
  type FolderImpact,
  type FolderOpResult,
  type MoveFolderRequest,
  type RenameFolderRequest,
  folderDeletePreviewSchema,
  folderDeleteResultSchema,
  folderImpactSchema,
  folderOpResultSchema,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';

/**
 * Client typé des ÉCRITURES SUR LES DOSSIERS (XPHOTO-32, lots 4 et 5) : créer, renommer,
 * déplacer, supprimer, et les deux LECTURES qui alimentent la fenêtre de confirmation.
 *
 * Comme les autres clients du projet, chaque réponse est RE-PARSÉE via son schéma
 * partagé `@xphoto/shared` (« never trust the client », y compris ses propres réponses).
 *
 * Ces trois routes sont SYNCHRONES : elles rendent le résultat, pas un job. Aucun suivi
 * dans la cloche de notifications — il n'y a rien à suivre (cf. l'en-tête de
 * `packages/shared/src/folder-ops.ts`).
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

async function postFolderOp(
  libraryId: string,
  suffix: '' | '/rename' | '/move',
  body: CreateFolderRequest | RenameFolderRequest | MoveFolderRequest,
): Promise<FolderOpResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/folders${suffix}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return folderOpResultSchema.parse(await readJson(res));
}

/** `POST /api/libraries/:id/folders` — crée un sous-dossier vide (201). */
export async function createFolder(
  libraryId: string,
  body: CreateFolderRequest,
): Promise<FolderOpResult> {
  return postFolderOp(libraryId, '', body);
}

/** `POST /api/libraries/:id/folders/rename` — renomme un dossier (parent inchangé). */
export async function renameFolder(
  libraryId: string,
  body: RenameFolderRequest,
): Promise<FolderOpResult> {
  return postFolderOp(libraryId, '/rename', body);
}

/** `POST /api/libraries/:id/folders/move` — déplace un dossier (nom conservé). */
export async function moveFolder(
  libraryId: string,
  body: MoveFolderRequest,
): Promise<FolderOpResult> {
  return postFolderOp(libraryId, '/move', body);
}

/**
 * `POST /api/libraries/:id/folders/delete` — supprime le dossier et son sous-arbre
 * (XPHOTO-32 lot 5). `removeSystemFiles` autorise, et seulement alors, l'effacement des
 * fichiers de la liste blanche (`.DS_Store`…) qui empêchent seuls le `rmdir`.
 */
export async function deleteFolder(
  libraryId: string,
  body: DeleteFolderRequest,
): Promise<FolderDeleteResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/folders/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return folderDeleteResultSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/folder-impact?path=…` — ce qu'un renommage entraînerait :
 * médias / partages publics / accès qui SUIVRONT, et filtres enregistrés qui NE
 * SUIVRONT PAS (le seul avertissement à afficher).
 */
export async function fetchFolderImpact(
  libraryId: string,
  path: string,
  signal?: AbortSignal,
): Promise<FolderImpact> {
  const res = await apiFetch(
    `/api/libraries/${libraryId}/folder-impact?path=${encodeURIComponent(path)}`,
    signal ? { signal } : {},
  );
  return folderImpactSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/folder-delete-preview?path=…` — ce qu'une suppression
 * entraînerait (répertoires, fichiers système et leurs NOMS) ou pourquoi elle sera
 * refusée (`blockers`).
 *
 * ⚠ Cet aperçu ne décide de rien : le serveur RE-CALCULE tout au moment d'agir. Il sert
 * à ce que l'utilisateur sache AVANT de cliquer, pas à raccourcir le travail du serveur.
 */
export async function fetchFolderDeletePreview(
  libraryId: string,
  path: string,
  signal?: AbortSignal,
): Promise<FolderDeletePreview> {
  const res = await apiFetch(
    `/api/libraries/${libraryId}/folder-delete-preview?path=${encodeURIComponent(path)}`,
    signal ? { signal } : {},
  );
  return folderDeletePreviewSchema.parse(await readJson(res));
}
